<template>
    <div id="mailbox" v-if="IsEmailEnabled()">
        <AppBreadCrumb :paths="[{ title: $t('message.mail') }, ...paths]" :show-trial-remain-day="true" />
        <div class="route-page-content breadcrumb-content">
            <SidebarChild
                :selectedItem="selectedTab"
                :items="menuItems"
                :focusActive="true"
                @selected="handleSidebarSelection"
            />
            <div class="page-content-wrapper">
                <div id="mailbox-in" v-if="isSelected('inbox')">
                    <MailboxMenu @mailDetail="(e) => (mailDetails = e)" style="min-width: 15%" />

                    <a-spin :spinning="loading">
                        <exampleView v-if="selectedNewTemplate" :userId="user._id" />

                        <MailList
                            :isRefresh="refresh"
                            v-if="!selectedNewTemplate"
                            @mailDetails="(e) => (mailDetails = e)"
                            @loading="(e) => (loading = e)"
                            style="min-width: 27%; max-width: 27%"
                        />

                        <MailComposer
                            v-if="selectedNewMail && !selectedNewTemplate"
                            :mailDetails="mailDetails"
                            @newMailDetail="(e) => (newMailDetail = e)"
                            :isNewMail="true"
                            replyContent=""
                            style="width: 100%"
                        />

                        <NoMessage
                            v-if="!selectedNewMail && mailDetails.length === 0 && !selectedNewTemplate"
                            :desc="$t('mail.mailNotFound')"
                            newMail="true"
                            class="empty-mail"
                            style="width: 100%"
                        />

                        <ThreadViewer
                            v-if="!selectedNewMail && mailDetails.length > 0 && !selectedNewTemplate"
                            :mailDetails="mailDetails"
                            style="width: 100%"
                        />
                    </a-spin>
                </div>
                <EmailTemplates v-if="isSelected('templates')" />
                <EmailBroadcastList v-if="isSelected('broadcast')" />
                <EmailCustomerGroups v-if="isSelected('customer-groups')" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import { log2 } from "../../../utils/log2";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb";
import MailList from "./MailList";
import MailboxMenu from "./MailboxMenu";
import MailComposer from "./MailComposer.vue";
import ThreadViewer from "./ThreadViewer";
import * as _ from "lodash";
import { newMailSound } from "../../../utils/notificationSounds";
import CONFIG from "../../../config/config";
import exampleView from "./views/Example.vue";
import NoMessage from "./NoMessage.vue";
import SidebarChild from "../../../components/Sidebar/SidebarChild.vue";
import EmailTemplates from "./templates/EmailTemplates.vue";
import EmailBroadcastList from "./broadcast/EmailBroadcastList.vue";
import EmailCustomerGroups from "./customer-groups/CustomerGroups.vue";
import ChatbotMenuItems from "../Chatbot/ChatbotMenuItems";

const MAIL_NEW_FEATURES_ENABLED = [
    "local.supsis.live",
    "beta.supsis.live",
    "supsis.live",
    "ataksms.supsis.live",
    "ertugrul.supsis.live",
    "turcaf.supsis.live",
    "kobiturkiye.supsis.live"
].includes(window.location.hostname);
const log = log2("Mail");
export default {
    name: "Mail",
    components: {
        EmailCustomerGroups,
        EmailBroadcastList,
        EmailTemplates,
        SidebarChild,
        NoMessage,
        exampleView,
        AppBreadCrumb,
        MailList,
        MailboxMenu,
        MailComposer,
        ThreadViewer,
    },
    props: {},
    data() {
        return {
            defaultPath: this.$route.path,
            paths: [],
            queryAction: this.$route.query.action,
            mailDetails: [],
            newMailDetail: [],
            selectedRepresentativeId: "",
            loading: "",
            refresh: 0,
            selectedTab: this.$route.path.replace(/(\/mails\/|\/mails)/g, "") || "inbox",
            menuItems: [
                {
                    enabled: true,
                    key: "inbox",
                    label: this.$t("mail.inbox"),
                    icon: {
                        type: "material-icons",
                        name: "email",
                    },
                },
                {
                    enabled: MAIL_NEW_FEATURES_ENABLED,
                    key: "templates",
                    label: this.$t("mail.templates"),
                    icon: {
                        type: "material-icons",
                        name: "drafts",
                    },
                },
                {
                    enabled: MAIL_NEW_FEATURES_ENABLED,
                    key: "broadcast",
                    label: this.$t("mail.broadcast"),
                    icon: {
                        type: "material-icons",
                        name: "cell_tower",
                    },
                },
                {
                    enabled: MAIL_NEW_FEATURES_ENABLED,
                    key: "customer-groups",
                    label: this.$t("wp.customerGroups"),
                    icon: {
                        type: "material-icons",
                        name: "groups",
                    },
                },
            ].filter((item) => item.enabled),
        };
    },
    computed: {
        ...mapState({
            userInteracted: (state) => state.app.userInteracted,
            selectedNewMail: (state) => state.mail.selectedNewMail,
            selectedNewTemplate: (state) => state.mail.selectedNewTemplate,
            selectedMenu: (state) => state.mail.selectedMenu,
            selectedDepartment: (state) => state.mail.selectedDepartment,
            selectedMailThreadId: (state) => state.mail.selectedMailThreadId,
            emailNotification: (state) => state.mail.emailNotification,
            emailSendSuccess: (state) => state.mail.emailSendSuccess,
            user: (state) => state.auth.user,
        }),
        ...mapGetters({}),
    },
    methods: {
        ...mapMutations({
            CHANGE_SELECTED_MENU: "mail/CHANGE_SELECTED_MENU",
            CHANGE_SELECTED_DEPARTMENT: "mail/CHANGE_SELECTED_DEPARTMENT",
            SELECTED_MAIL: "mail/SELECTED_MAIL",
            playEmailNotification: "mail/playEmailNotification",
            SELECTED_NEW_MAIL: "mail/SELECTED_NEW_MAIL",
        }),
        playNotificationSound: _.debounce(function (isReceive = false) {
            newMailSound().play();
        }, 1000),
        IsEmailEnabled() {
            return CONFIG.EMAIL_SERVICE_ENABLED;
        },
        handleSidebarSelection(item) {
            this.selectedTab = item.key;
        },
        isSelected(type) {
            const items = this.menuItems;
            const item = items.find((item) => item?.parentKey === this.selectedTab || item.key === this.selectedTab);
            return item.key === type || item.parentKey === type;
        },
        setDefaultPath: _.debounce(function () {
            this.defaultPath = this.$route.path;
        }, 0),
        init() {
            this.selectedTab = this.defaultPath.replace(/(\/mails\/|\/mails)/g, "") || "inbox";
            const selected = this.menuItems.find((x) => x.key === this.selectedTab);
            if (!selected?.enabled) {
                const available = this.menuItems.find((x) => x.enabled);
                if (available) {
                    this.selectedTab = available.key;
                } else {
                    this.$router.push("/denied");
                }
            }
        },
    },
    async mounted() {
        const { selected, departmentName, threadId } = this.$route.query;
        if (!!selected) {
            this.CHANGE_SELECTED_MENU(selected);
        } else {
            this.CHANGE_SELECTED_MENU("My");
            this.paths = [{ title: "My" }];
            this.$router.replace({
                query: { ["selected"]: encodeURI("My") },
            });
        }
        if (!!departmentName) {
            this.CHANGE_SELECTED_DEPARTMENT(departmentName);
        } else {
            this.CHANGE_SELECTED_DEPARTMENT("");
        }
        if (!!threadId) {
            this.SELECTED_MAIL(threadId);
        } else {
            this.mailDetails = [];
        }
        if (this.queryAction === "create") this.SELECTED_NEW_MAIL(true);

        window.addEventListener("keydown", (e) => {
            if ("Escape" === e.key || "Esc" === e.key) {
                this.mailDetails = [];
                this.SELECTED_NEW_MAIL(false);
            }
        });
    },
    created() {
        this.init();
    },
    watch: {
        newMailDetail(val) {
            this.mailDetails = [val];
        },
        emailNotification(val) {
            if (val === true) {
                this.$message.success(this.$t("mail.newMailArrived"), 3);
                if (this.userInteracted) {
                    this.playNotificationSound();
                }
                this.playEmailNotification(false);
            }
        },
        emailSendSuccess(val) {
            this.$message.destroy();
            this.$message.success(this.$t("mail.mailSentSucs"), 3);
            this.mailDetails = [];
            this.refresh += 1;
        },
        selectedMenu(val) {
            if (!!val) {
                this.paths = [{ title: val }];
                this.$router.replace({
                    query: { ["selected"]: encodeURI(val) },
                });
            }
        },
        selectedDepartment(val) {
            if (val === "" || val === undefined) return;
            this.paths = [{ title: this.selectedMenu }, { title: val }];
            this.$router.replace({
                query: { ["selected"]: encodeURI("department"), ["departmentName"]: encodeURI(val) },
            });
        },
        selectedMailThreadId(val) {
            console.log("watch selectedMailThreadId worked");
            if (this.selectedMenu === "department") {
                this.paths = [{ title: this.selectedMenu }, { title: this.selectedDepartment }, { title: val }];
            } else {
                this.paths = [{ title: this.selectedMenu }, { title: val }];
            }
            let query = Object.assign({}, this.$route.query);
            this.$router.replace({
                query: { ...query, ["threadId"]: encodeURI(val) },
            });
        },
        selectedTab(newVal) {
            this.$router.push(`/mails/${newVal}`);
        },
        "$route.path": {
            deep: true,
            handler(val) {
                this.setDefaultPath(val);
            },
        },
        defaultPath(val) {
            this.init(val);
        },
    },
};
</script>

<style lang="scss">
#mailbox {
    background-color: #ffffff;
    width: 100%;

    .route-page-content {
        width: 100%;
    }

    .ant-spin-nested-loading {
        width: 100% !important;
        display: flex !important;
    }

    .ant-spin-container {
        width: 100% !important;
        display: flex !important;
    }

    #mailbox-in {
        display: flex;
        position: relative;
        width: 100%;

        .empty-mail {
            justify-content: center;
            width: 100%;
            display: flex;
            align-items: center;
        }

        .mail-composer {
            width: 100%;
            border-right: 1px solid var(--color-border-default);
        }
    }
}
</style>
